/**
 * ErrorBoundary — class component that catches render-time errors from its
 * children and shows a readable fallback instead of a blank page.
 *
 * Added Apr 23, 2026 to fix Bug 1 (Warehouse/Inventory page rendering blank):
 *   silent runtime errors inside lazy-loaded pages were leaving `<main>` empty
 *   with no indication to the user. The boundary now renders the error in a
 *   recoverable panel with a reload button + logs to console so field reports
 *   can quote the message.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { tryAutoReload } from '../utils/chunkReload';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback (e.g. page name). */
  label?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // If this is a stale-chunk error from a mid-session deploy, try a one-shot
    // hard reload (sessionStorage-guarded to avoid infinite loops). If the
    // reload fires, we don't bother setting component state — the page is
    // about to tear down anyway.
    if (tryAutoReload(error)) return;

    // Always log to console so support can copy-paste stacks from customers.
    // eslint-disable-next-line no-console
    console.error('[AURA ErrorBoundary]', this.props.label ?? 'unlabelled', error, info);
    this.setState({ info });
  }

  reset = (): void => {
    this.setState({ error: null, info: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="p-6">
        <div
          className="mx-auto max-w-3xl rounded-lg border p-6"
          style={{
            background: 'var(--ivory-0, #fff)',
            borderColor: 'var(--line, #e5e7eb)',
          }}
        >
          <h2 className="mb-2 text-lg font-semibold" style={{ color: 'var(--ink-0, #111)' }}>
            Something went wrong while rendering{this.props.label ? ` ${this.props.label}` : ''}.
          </h2>
          <p className="mb-4 text-sm" style={{ color: 'var(--ink-3, #555)' }}>
            The error has been logged to the browser console. You can retry the page or refresh.
          </p>
          <pre
            className="mb-4 overflow-auto rounded p-3 text-xs"
            style={{
              background: 'var(--ivory-2, #fafafa)',
              border: '1px solid var(--line, #e5e7eb)',
              maxHeight: 240,
            }}
          >
            {String(this.state.error?.message ?? this.state.error)}
          </pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border px-3 py-1.5 text-sm"
              style={{
                background: 'var(--saffron, #c9831b)',
                color: '#fff',
                borderColor: 'var(--saffron, #c9831b)',
              }}
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border px-3 py-1.5 text-sm"
              style={{
                background: 'transparent',
                color: 'var(--ink-1, #333)',
                borderColor: 'var(--line, #e5e7eb)',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
